/**
 * <coverage-calculator>
 *
 * Owns the "Did you know? ... covers up to:" panel above the add-to-cart button.
 *
 * Reads a JSON payload emitted by snippets/coverage-info.liquid, keyed by variant id, and
 * re-renders on three inputs:
 *   - variant:changed  (fired on the product form by the theme's variant picker)
 *   - the quantity input, to show a running total for the selected quantity
 *   - the area input, to recommend how many units cover a given area
 *
 * Follows the theme's existing custom-element convention: the owning form is found via the
 * form-id attribute, matching product-inventory and payment-container.
 */
/**
 * Container ladder, largest first. 4 pints = 1 quart, 4 quarts = 1 gallon, 5 gallons = 1 pail,
 * so everything is expressed in pints and the breakdown is a greedy walk down this list.
 *
 * Order matters: "5 Gallon" must be tested before "Gallon" or it would match the smaller size.
 */
const COVERAGE_SIZES = [
  { match: /5\s*[- ]?\s*gal/i, pints: 80, label: '5 Gallon Pail' },
  { match: /gal/i, pints: 16, label: '1 Gallon' },
  { match: /quart|\bqt\b/i, pints: 4, label: '1 Quart' },
  { match: /pint|\bpt\b/i, pints: 1, label: '1 Pint' },
];

class CoverageCalculator extends HTMLElement {
  connectedCallback() {
    const script = this.querySelector('[data-coverage-data]');
    if (!script) return;

    try {
      this.data = JSON.parse(script.textContent).variants || {};
    } catch (error) {
      // A malformed payload must not take the rest of the product page down with it.
      console.warn('[coverage-calculator] could not parse coverage data', error);
      this.hidden = true;
      return;
    }

    this.variantId = this.dataset.selectedVariant;
    this.showTotal = this.hasAttribute('data-show-total');
    this.showArea = this.hasAttribute('data-show-area-input');

    this.listEl = this.querySelector('[data-coverage-list]');
    this.titleEl = this.querySelector('[data-coverage-variant-title]');
    this.remarkEl = this.querySelector('[data-coverage-remark]');
    this.totalEl = this.querySelector('[data-coverage-total]');
    this.areaEl = this.querySelector('[data-coverage-area]');
    this.surfaceEl = this.querySelector('[data-coverage-surface]');
    this.resultEl = this.querySelector('[data-coverage-result]');

    this.form = document.getElementById(this.getAttribute('form-id'));
    this.quantityEl = this.form ? this.form.querySelector('[name="quantity"]') : null;

    if (this.form) {
      this.form.addEventListener('variant:changed', this._onVariantChanged.bind(this));
    }
    if (this.quantityEl) {
      this.quantityEl.addEventListener('input', this._onQuantityChanged.bind(this));
      this.quantityEl.addEventListener('change', this._onQuantityChanged.bind(this));
    }
    if (this.areaEl) {
      this.areaEl.addEventListener('input', this._onAreaChanged.bind(this));
    }
    if (this.surfaceEl) {
      this.surfaceEl.addEventListener('change', this._onAreaChanged.bind(this));
    }

    this.render();
  }

  /**
   * Pulls the container size out of a variant title, matching the Liquid. "1 Gallon / Clear" must
   * read as "1 Gallon", not "Clear", so prefer the part that names a container.
   */
  sizeLabel(title) {
    const parts = String(title).split('(')[0].split('/').map((part) => part.trim());
    const named = parts.find((part) => COVERAGE_SIZES.some((size) => size.match.test(part)));
    return named || parts[parts.length - 1] || '';
  }

  get current() {
    return this.data[this.variantId] || null;
  }

  get quantity() {
    const value = parseInt(this.quantityEl && this.quantityEl.value, 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  _onVariantChanged(event) {
    const variant = event.detail && event.detail.variant;
    if (!variant) {
      this.hidden = true;
      return;
    }
    this.variantId = String(variant.id);
    this.hidden = false;
    this.render();
  }

  _onQuantityChanged() {
    this.renderTotal();
  }

  render() {
    const data = this.current;

    // A variant with no coverage data should hide the panel rather than show an empty shell.
    if (!data || !data.surfaces || data.surfaces.length === 0) {
      this.hidden = true;
      return;
    }
    this.hidden = false;

    if (this.titleEl) {
      this.titleEl.textContent = this.sizeLabel(data.title);
    }

    if (this.listEl) {
      this.listEl.innerHTML = '';
      data.surfaces.forEach((surface) => {
        const row = document.createElement('div');
        const label = document.createTextNode(`${surface.label}: `);
        const value = document.createElement('span');
        value.textContent = `${this.formatCoverage(surface)}*`;
        row.appendChild(label);
        row.appendChild(value);
        this.listEl.appendChild(row);
      });
    }

    if (this.remarkEl) {
      const coats = data.coats;
      const coatText = coats ? `* Based on ${coats} ${coats == 1 ? 'Coat' : 'Coats'}. ` : '* ';
      this.remarkEl.textContent = `${coatText}Coverage may vary based on surface porosity and application method.`;
    }

    this.renderSurfaceOptions();
    this.renderTotal();
    this.renderResult();
  }

  /**
   * Legacy string metafields carry their own wording ("up to 15 sq ft"); JSON entries carry a
   * bare number, so only those need the unit appended.
   */
  formatCoverage(surface) {
    if (surface.raw) return surface.raw;
    const unit = this.current.unit || 'sq ft';
    return `up to ${this.round(surface.sqft)} ${unit}`;
  }

  renderTotal() {
    if (!this.showTotal || !this.totalEl) return;

    const data = this.current;
    const quantity = this.quantity;

    if (!data || quantity <= 1) {
      this.totalEl.hidden = true;
      return;
    }

    const best = data.surfaces.reduce((a, b) => (Number(b.sqft) > Number(a.sqft) ? b : a));
    const worst = data.surfaces.reduce((a, b) => (Number(b.sqft) < Number(a.sqft) ? b : a));
    const unit = data.unit || 'sq ft';

    const low = this.round(Number(worst.sqft) * quantity);
    const high = this.round(Number(best.sqft) * quantity);
    const range = low === high ? `${high} ${unit}` : `${low}–${high} ${unit}`;

    this.totalEl.textContent = `${quantity} × ${this.titleEl ? this.titleEl.textContent : 'unit'} covers approx. ${range} total, depending on surface.`;
    this.totalEl.hidden = false;
  }

  renderSurfaceOptions() {
    if (!this.showArea || !this.surfaceEl) return;

    const data = this.current;
    const previous = this.surfaceEl.value;
    this.surfaceEl.innerHTML = '';

    data.surfaces.forEach((surface, index) => {
      const option = document.createElement('option');
      option.value = String(index);
      option.textContent = surface.label;
      this.surfaceEl.appendChild(option);
    });

    // Keep the shopper's chosen surface across a variant switch where the labels still line up.
    const stillValid = previous !== '' && Number(previous) < data.surfaces.length;
    if (stillValid) this.surfaceEl.value = previous;
  }

  _onAreaChanged() {
    this.renderResult();
  }

  renderResult() {
    if (!this.showArea || !this.resultEl) return;

    const data = this.current;
    const area = parseFloat(this.areaEl && this.areaEl.value);
    const surface = data.surfaces[Number(this.surfaceEl && this.surfaceEl.value) || 0];

    if (!Number.isFinite(area) || area <= 0 || !surface || !Number(surface.sqft)) {
      this.resultEl.textContent = '';
      return;
    }

    const unit = data.unit || 'sq ft';
    const needed = Math.ceil(area / Number(surface.sqft));

    const unitName = this.titleEl ? this.titleEl.textContent : 'unit';
    const packs = this.packBreakdown(needed, unitName);
    const areaText = this.formatNumber(this.round(area));

    // State the containers to buy directly; the raw count in the selected size is not useful on
    // its own once it has been converted into larger packs.
    this.resultEl.textContent = packs
      ? `For ${areaText} ${unit} of ${surface.label} you need ${packs}.`
      : `For ${areaText} ${unit} of ${surface.label} you need ${needed} × ${unitName}.`;
  }

  /**
   * Restates "14 × 1 Pint" as "3 Quarts + 2 Pints" by converting to pints and walking the ladder.
   *
   * Returns null when there is nothing useful to add: an unrecognised container size, or a
   * breakdown that is just the same thing again (7 × 1 Gallon is already "7 Gallons").
   */
  /**
   * Classifies a variant title into exactly one container size. Order matters here too: a
   * "5 Gallon" variant must resolve to the pail, not to Gallon, or the ladder would think the
   * product sells single gallons when it does not.
   */
  sizeOf(title) {
    return COVERAGE_SIZES.find((candidate) => candidate.match.test(title)) || null;
  }

  /**
   * The subset of the ladder this product actually sells, so the calculator never recommends a
   * 5 Gallon Pail for a product only offered in pints and quarts.
   */
  get availableSizes() {
    if (!this._availableSizes) {
      const present = new Set();
      Object.values(this.data).forEach((variant) => {
        const size = this.sizeOf(variant.title);
        if (size) present.add(size);
      });
      this._availableSizes = COVERAGE_SIZES.filter((size) => present.has(size));
    }
    return this._availableSizes;
  }

  packBreakdown(needed, unitName) {
    const size = this.sizeOf(unitName);
    if (!size) return null;

    const ladder = this.availableSizes.length ? this.availableSizes : COVERAGE_SIZES;

    let remaining = needed * size.pints;
    const parts = [];

    ladder.forEach((candidate) => {
      const count = Math.floor(remaining / candidate.pints);
      if (count > 0) {
        parts.push(`${count} × ${candidate.label}`);
        remaining -= count * candidate.pints;
      }
    });

    if (parts.length === 0) return null;
    // Single term in the same container the shopper already sees adds nothing.
    if (parts.length === 1 && this.sizeOf(parts[0]) === size) return null;

    return parts.join(' + ');
  }

  round(value) {
    return Math.round(Number(value) * 10) / 10;
  }

  /** Thousands separators, so 5000 reads as 5,000. */
  formatNumber(value) {
    return Number(value).toLocaleString('en-US');
  }
}

if (!window.customElements.get('coverage-calculator')) {
  window.customElements.define('coverage-calculator', CoverageCalculator);
}
